/**
 * Aba "Prompts salvos" da personalização de IA.
 *
 * Prompt salvo é texto reutilizável acionado SOB DEMANDA no chat (título curto
 * + corpo). Não é injetado sozinho: quando o advogado clica, ele entra no
 * composer como mensagem dele. `usos` ordena os atalhos que aparecem na
 * abertura da conversa.
 */
import { MessageSquarePlus, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { IaPrompt } from '@/api/iaPersonalizacao';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import {
  useAtualizarPrompt,
  useCriarPrompt,
  useExcluirPrompt,
  usePromptsIa,
} from '@/hooks/useIaPersonalizacao';
import { classifyError } from '@/lib/errors';

const MAX_TITULO = 120;
const MAX_TEXTO = 8000;

export function PromptsTab() {
  const { data, isLoading, isError, error, refetch } = usePromptsIa();
  const criar = useCriarPrompt();
  const atualizar = useAtualizarPrompt();
  const excluir = useExcluirPrompt();

  const [editor, setEditor] = useState<{ id: string | null; titulo: string; texto: string } | null>(null);
  const [confirmarExclusao, setConfirmarExclusao] = useState<IaPrompt | null>(null);

  const prompts = data?.prompts ?? [];
  const salvando = criar.isPending || atualizar.isPending;

  function salvar() {
    if (!editor) return;
    const titulo = editor.titulo.trim();
    const texto = editor.texto.trim();
    if (!titulo || !texto) return;
    const fechar = { onSuccess: () => setEditor(null) };
    if (editor.id) {
      atualizar.mutate({ id: editor.id, titulo, texto }, fechar);
    } else {
      criar.mutate({ titulo, texto }, fechar);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size={32} label="Carregando prompts" />
      </div>
    );
  }

  if (isError) {
    const { message } = classifyError(error);
    return (
      <ErrorState
        title="Não foi possível carregar seus prompts"
        description={message}
        actions={[{ label: 'Tentar novamente', onClick: () => refetch() }]}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Alert>
        <MessageSquarePlus className="h-4 w-4" />
        <AlertDescription>
          Pedidos que você repete — &ldquo;resuma os autos em ordem cronológica&rdquo;, &ldquo;liste os pedidos e
          o que já foi deferido&rdquo;. Eles aparecem como atalhos ao abrir uma conversa, os mais usados primeiro.
        </AlertDescription>
      </Alert>

      <div className="flex justify-end">
        <Button onClick={() => setEditor({ id: null, titulo: '', texto: '' })}>
          <MessageSquarePlus className="mr-2 h-4 w-4" />
          Novo prompt
        </Button>
      </div>

      {prompts.length === 0 ? (
        <EmptyState
          icon={<MessageSquarePlus />}
          title="Nenhum prompt salvo"
          description="Guarde os pedidos que você faz sempre e acione-os com um clique no chat."
        />
      ) : (
        <ul className="space-y-2">
          {prompts.map((p) => (
            <li key={p.id}>
              <Card>
                <CardContent className="flex items-start gap-3 py-4">
                  <div className="min-w-0 flex-1">
                    <p className="break-words font-medium text-foreground">{p.titulo}</p>
                    <p className="mt-1 line-clamp-2 whitespace-pre-wrap break-words text-sm text-muted-foreground">
                      {p.texto}
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {p.usos === 0 ? 'Ainda não usado' : p.usos === 1 ? 'Usado 1 vez' : `Usado ${p.usos} vezes`}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Editar prompt"
                      onClick={() => setEditor({ id: p.id, titulo: p.titulo, texto: p.texto })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Excluir prompt"
                      onClick={() => setConfirmarExclusao(p)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={editor !== null} onOpenChange={(aberto) => !aberto && setEditor(null)}>
        {/* Largo e alto: prompt de trabalho tem dezenas de linhas, e editar num
            campo de 6 linhas obriga a rolar dentro do campo para reler o que se
            escreveu. */}
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editor?.id ? 'Editar prompt' : 'Novo prompt'}</DialogTitle>
            <DialogDescription>
              O título aparece no atalho; o texto é o que vai para a conversa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prompt-titulo">Título</Label>
              <Input
                id="prompt-titulo"
                value={editor?.titulo ?? ''}
                onChange={(e) =>
                  setEditor((atual) => (atual ? { ...atual, titulo: e.target.value.slice(0, MAX_TITULO) } : atual))
                }
                placeholder="Ex.: Resumo cronológico"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prompt-texto">Texto</Label>
              <Textarea
                id="prompt-texto"
                rows={18}
                // Cresce com a janela, mas nunca empurra o rodapé do diálogo
                // para fora da tela; `resize-y` deixa o usuário ajustar.
                className="max-h-[55vh] min-h-[280px] resize-y"
                value={editor?.texto ?? ''}
                onChange={(e) =>
                  setEditor((atual) => (atual ? { ...atual, texto: e.target.value.slice(0, MAX_TEXTO) } : atual))
                }
                placeholder="Ex.: Resuma os autos em ordem cronológica, com as datas e o número de cada peça."
              />
              {/* O teto é o mesmo da mensagem do chat — o prompt VIRA a mensagem
                  do turno. Melhor ver a régua enquanto escreve do que descobrir
                  o corte ao colar um prompt longo. */}
              <p className="text-right text-xs text-muted-foreground">
                {(editor?.texto ?? '').length.toLocaleString('pt-BR')} / {MAX_TEXTO.toLocaleString('pt-BR')}{' '}
                caracteres
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditor(null)}>
              Cancelar
            </Button>
            <Button onClick={salvar} disabled={!editor?.titulo.trim() || !editor?.texto.trim() || salvando}>
              {salvando && <Spinner className="mr-2 text-inherit" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmarExclusao !== null}
        title="Excluir prompt"
        message="O atalho deixa de aparecer no chat. Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        confirmColor="error"
        loading={excluir.isPending}
        onCancel={() => setConfirmarExclusao(null)}
        onConfirm={() => {
          if (!confirmarExclusao) return;
          excluir.mutate(confirmarExclusao.id, { onSuccess: () => setConfirmarExclusao(null) });
        }}
      />
    </div>
  );
}
